/**
 * GuidedTourProvider - decides which tour (if any) starts on first open,
 * runs it, and remembers the outcome per person on the server.
 *
 * First open: the first tour in the list that matches one of the person's
 * roles and that they have not finished or closed at its current version
 * starts once the app has settled - unless tours are switched off bank-wide
 * (SystemSettings tours.enabled). Everything else is available from Help
 * through useGuidedTour().start(id), switched on or not.
 */
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { TourOverlay } from './TourOverlay';
import type { TourDef, TourProgress, TourStore } from './types';
import './GuidedTour.css';

const AUTO_START_DELAY_MS = 1500;

export interface GuidedTourContextValue {
  /** Tours for this person's roles, in priority order. */
  tours: ReadonlyArray<TourDef>;
  progress: ReadonlyArray<TourProgress>;
  activeTourId: string | null;
  start: (tourId: string) => void;
}

const Ctx = createContext<GuidedTourContextValue>({ tours: [], progress: [], activeTourId: null, start: () => undefined });

function done(t: TourDef, progress: ReadonlyArray<TourProgress>): boolean {
  return progress.some((p) => p.tourId === t.id && p.version >= t.version);
}

export function useGuidedTour(): GuidedTourContextValue {
  return useContext(Ctx);
}

export interface GuidedTourProviderProps {
  tours: ReadonlyArray<TourDef>;
  /** The person's role codes; null while sign-in is still resolving (nothing auto-starts). */
  roles: ReadonlyArray<string> | null;
  store: TourStore | null;
  navigate: (path: string) => void;
  /** Current route path, for tours with startOnPath. */
  currentPath?: string;
  /** Host can hold the first-run start back (e.g. while a blocking gate is on screen). Default true. */
  autoStart?: boolean;
  children: ReactNode;
}

export function GuidedTourProvider({ tours, roles, store, navigate, currentPath = '', autoStart = true, children }: GuidedTourProviderProps): JSX.Element {
  const [progress, setProgress] = useState<TourProgress[]>([]);
  const [enabled, setEnabled] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [active, setActive] = useState<{ id: string; step: number } | null>(null);
  const autoTried = useRef(false);
  const pathTried = useRef(new Set<string>());

  const mine = useMemo(() => {
    if (!roles) return [];
    const have = new Set(roles.map((r) => String(r).toLowerCase()));
    return tours.filter((t) => t.roles.some((r) => have.has(r.toLowerCase())));
  }, [tours, roles]);

  useEffect(() => {
    if (!store || !roles || loaded) return;
    let live = true;
    store.load()
      .then((r) => { if (live) { setEnabled(r.enabled); setProgress([...r.progress]); } })
      .catch(() => { /* no progress store reachable: tours stay available from Help, nothing auto-starts */ })
      .finally(() => { if (live) setLoaded(true); });
    return () => { live = false; };
  }, [store, roles, loaded]);

  // First open: the first matching, not-yet-done tour without a page trigger.
  useEffect(() => {
    if (!loaded || !enabled || !autoStart || autoTried.current || active) return undefined;
    autoTried.current = true;
    const first = mine.find((t) => !t.startOnPath && !done(t, progress));
    if (!first) return undefined;
    const timer = window.setTimeout(() => setActive((a) => a ?? { id: first.id, step: 0 }), AUTO_START_DELAY_MS);
    return () => window.clearTimeout(timer);
  }, [loaded, enabled, autoStart, mine, progress, active]);

  // Page-triggered tours: once each, the first time the person reaches a matching page.
  useEffect(() => {
    if (!loaded || !enabled || !autoStart || active) return undefined;
    const hit = mine.find((t) => t.startOnPath && t.startOnPath.test(currentPath) && !done(t, progress) && !pathTried.current.has(t.id));
    if (!hit) return undefined;
    pathTried.current.add(hit.id);
    const timer = window.setTimeout(() => setActive((a) => a ?? { id: hit.id, step: 0 }), AUTO_START_DELAY_MS);
    return () => window.clearTimeout(timer);
  }, [loaded, enabled, autoStart, mine, progress, active, currentPath]);

  const tour = active ? tours.find((t) => t.id === active.id) ?? null : null;

  const finish = useCallback((status: 'completed' | 'dismissed') => {
    if (!active || !tour) return;
    const row: TourProgress = { tourId: tour.id, version: tour.version, status, lastStep: active.step };
    setProgress((p) => [...p.filter((x) => x.tourId !== tour.id), row]);
    setActive(null);
    if (store) void store.record(tour.id, tour.version, status, active.step).catch(() => undefined);
  }, [active, tour, store]);

  const onNext = useCallback(() => {
    if (!active || !tour) return;
    if (active.step >= tour.steps.length - 1) finish('completed');
    else setActive({ id: active.id, step: active.step + 1 });
  }, [active, tour, finish]);
  const onBack = useCallback(() => {
    setActive((a) => (a && a.step > 0 ? { id: a.id, step: a.step - 1 } : a));
  }, []);
  const onClose = useCallback(() => finish(active && tour && active.step >= tour.steps.length - 1 ? 'completed' : 'dismissed'), [finish, active, tour]);

  const start = useCallback((tourId: string) => {
    autoTried.current = true;
    if (tours.some((t) => t.id === tourId)) setActive({ id: tourId, step: 0 });
  }, [tours]);

  const value = useMemo<GuidedTourContextValue>(() => ({ tours: mine, progress, activeTourId: active?.id ?? null, start }),
    [mine, progress, active, start]);

  return (
    <Ctx.Provider value={value}>
      {children}
      {tour && active && (
        <TourOverlay tour={tour} step={active.step} navigate={navigate} onNext={onNext} onBack={onBack} onClose={onClose} />
      )}
    </Ctx.Provider>
  );
}

export default GuidedTourProvider;
